export const R6_CDC = Object.freeze({
  frameSize: 64,
  crcIndex: 63,
  baudRate: 9600,
  // 文档中的 R6 标准 USB 标识，保留旧候选值兼容已有测试设备。
  usbFilters: [
    { usbVendorId: 0x1915, usbProductId: 0x520b },
    { usbVendorId: 0x05ac, usbProductId: 0x0256 },
    { usbVendorId: 0x05ac, usbProductId: 0x024f },
    // AngryMiao dongle uses a CP210x USB-to-serial bridge.
    { usbVendorId: 0x10c4, usbProductId: 0xea60 },
  ],
  status: {
    success: 1,
    waiting: 2,
  },
  command: {
    product: 0x01,
    page: 0x02,
    key: 0x06,
  },
});

// USB identifiers only select candidate CDC ports. The product ID returned by
// 01 01 is the source of truth for the CB generation and its capabilities.
const CB_PRODUCT_MODELS = Object.freeze({
  CB01: "R1",
  CBR1: "R1",
  CB02: "R2",
  CBR2: "R2",
  CB03: "R3",
  CBR3: "R3",
  CB04: "R4",
  CBR4: "R4",
  CB05: "R5",
  CBR5: "R5",
  "CB R5": "R5",
  CB03_R5: "R5",
  CB_TERMINAL: "TERMINAL",
  CB_MATRIX: "MATRIX",
  CB_TM_01: "TMALL",
  CB06: "R6",
  CBR6: "R6",
  AM01R6: "R6",
  AMO1R6: "R6",
  R6: "R6",
});

function normalizeProductId(productId) {
  return String(productId || "")
    .trim()
    .toUpperCase();
}

export function getR6ProductCompatibility(productId) {
  const normalizedProductId = normalizeProductId(productId);
  const entry = CB_PRODUCT_MODELS[normalizedProductId];
  const model = typeof entry === "string" ? entry : null;
  const isR6 = model === "R6";
  return {
    family: model,
    model,
    productId: normalizedProductId,
    normalizedProductId,
    cdcSupported: model !== null && model !== "R1",
    audioSupported: isR6,
    firmwareSupported: isR6,
    fullJsonSupported: isR6,
  };
}

export function isSupportedR6Product(productId) {
  return getR6ProductCompatibility(productId).cdcSupported;
}

export function isSupportedR6ProfileProduct(productId) {
  return (
    normalizeProductId(productId) === "CB_XX" || isSupportedR6Product(productId)
  );
}

export function crc8(data) {
  let crc = 0;
  for (const byte of data) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
    }
  }
  return crc;
}

function toByte(value, name) {
  if (!Number.isInteger(value) || value < 0 || value > 0xff) {
    throw new RangeError(`${name} must be an integer between 0 and 255`);
  }
  return value;
}

function toBytes(value) {
  if (value instanceof Uint8Array) return value;
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  return Uint8Array.from(value || []);
}

export function buildFrame(command, subcommand, payload = []) {
  const bytes = toBytes(payload);
  if (bytes.length > R6_CDC.crcIndex - 2) {
    throw new RangeError("CDC payload exceeds one 64-byte frame");
  }

  const frame = new Uint8Array(R6_CDC.frameSize);
  frame[0] = toByte(command, "command");
  frame[1] = toByte(subcommand, "subcommand");
  frame.set(bytes, 2);
  frame[R6_CDC.crcIndex] = crc8(frame.subarray(0, R6_CDC.crcIndex));
  return frame;
}

export function isValidFrame(frame) {
  const bytes = toBytes(frame);
  return (
    bytes.length === R6_CDC.frameSize &&
    crc8(bytes.subarray(0, R6_CDC.crcIndex)) === bytes[R6_CDC.crcIndex]
  );
}

export function parseHexKey(value) {
  if (typeof value !== "string") {
    throw new TypeError("key code must be a hexadecimal string");
  }
  const hex = value.replace(/^#|^0x/i, "");
  if (!/^[0-9a-f]{8}$/i.test(hex)) {
    throw new TypeError(`invalid 4-byte key code: ${value}`);
  }
  return Uint8Array.from(hex.match(/../g).map((item) => parseInt(item, 16)));
}

function uint32Bytes(value) {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
    throw new RangeError("frame count must be an unsigned 32-bit integer");
  }
  return [value >>> 24, value >>> 16, value >>> 8, value & 0xff];
}

function uint16Bytes(value, name = "value") {
  if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
    throw new RangeError(`${name} must be an unsigned 16-bit integer`);
  }
  return [(value >> 8) & 0xff, value & 0xff];
}

function uint16LEBytes(value, name = "value") {
  if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
    throw new RangeError(`${name} must be an unsigned 16-bit integer`);
  }
  return [value & 0xff, (value >> 8) & 0xff];
}

function paddedKeys(values, count) {
  const result = [];
  for (let index = 0; index < count; index += 1) {
    result.push(...parseHexKey(values[index] || "#00000000"));
  }
  return result;
}

function paddedIntervals(values, count) {
  const result = [];
  for (let index = 0; index < count; index += 1) {
    result.push(...uint16Bytes(Number(values[index]) || 0));
  }
  return result;
}

export const frames = {
  productId: () => buildFrame(0x01, 0x01),
  productInfo: () => buildFrame(0x01, 0x02),
  setTime: (date = new Date()) => {
    const offsetMinutes = -date.getTimezoneOffset();
    const offsetHours = Math.floor(offsetMinutes / 60);
    // The wire offset stores whole hours; carry fractional hours in the timestamp.
    const timestamp =
      Math.floor(date.getTime() / 1000) +
      (offsetMinutes - offsetHours * 60) * 60;
    return buildFrame(0x01, 0x03, [
      ...uint32Bytes(timestamp),
      offsetMinutes > 0 ? 0 : offsetMinutes < 0 ? 1 : 2,
      toByte(Math.abs(offsetHours), "timezone hours"),
    ]);
  },
  pageCount: () => buildFrame(0x02, 0x06),
  jsonStart: () => buildFrame(0x01, 0x05),
  jsonEnd: (frameCount) => buildFrame(0x01, 0x06, uint32Bytes(frameCount)),
  enterUpgradeMode: () => buildFrame(0x01, 0x04),
  legacyKeyLayerRequest: () => buildFrame(0x06, 0x09, [0]),
  legacyMacroRequest: () => buildFrame(0x06, 0x0a, [0]),
  legacyPressHoldRequest: () => buildFrame(0x06, 0x0e, [0]),
  deviceReset: () => buildFrame(0x06, 0x11),
  speakerSwitch: (mask) =>
    buildFrame(0x07, 0x06, [toByte(mask, "speaker mask")]),
  enterTransportMode: () =>
    buildFrame(
      0x07,
      0x01,
      Array.from({ length: 0x3d }, (_, index) => index + 0x02)
    ),
  speakerTestMode: () => buildFrame(0x07, 0x07),
  speakerCalibrate: () => buildFrame(0x07, 0x04),
  noiseReduction: (mode) =>
    buildFrame(0x07, 0x08, [toByte(mode, "noise reduction mode")]),
  micSoundEffect: (mode) =>
    buildFrame(0x07, 0x09, [toByte(mode, "microphone sound effect mode")]),
  micDirection: (mode) =>
    buildFrame(0x07, 0x0a, [toByte(mode, "microphone direction mode")]),
  validDirectives: (pageCount, validBits) =>
    buildFrame(0x02, 0x05, [toByte(pageCount, "pageCount"), ...validBits]),
  uncertaintyInfo: (pageCount, pages) =>
    buildFrame(0x02, 0x01, [pageCount, ...pages]),
  pageControl: (frameCount, frameIndex, pageCount, pages) =>
    buildFrame(0x02, 0x02, [frameCount, frameIndex, pageCount, ...pages]),
  carLightInfo: (items) => buildFrame(0x0c, 0x01, items),
  carLightData: (pageIndex, frameIndex, usbFrameIndex, data) =>
    buildFrame(0x0c, 0x02, [
      pageIndex,
      (frameIndex >> 8) & 0xff,
      frameIndex & 0xff,
      usbFrameIndex,
      ...data,
    ]),
  wordPage: (frameIndex, pageIndex, valid, wordLength, unicode) =>
    buildFrame(0x03, 0x01, [
      frameIndex,
      pageIndex,
      valid ? 1 : 0,
      wordLength,
      ...unicode,
    ]),
  rgbFrame: (pageIndex, frameIndex, usbFrameIndex, data) =>
    buildFrame(0x04, pageIndex, [
      ...uint16LEBytes(frameIndex, "frameIndex"),
      usbFrameIndex,
      ...data,
    ]),
  keyFrame: (pageIndex, frameIndex, usbFrameIndex, data) =>
    buildFrame(0x05, pageIndex, [frameIndex, usbFrameIndex, ...data]),
  macroKey: (packTotal, packIndex, item, packetIndex) =>
    buildFrame(0x06, 0x05, [
      packTotal,
      packIndex,
      toByte(item.MACRO_key_index, "MACRO_key_index"),
      ...parseHexKey(item.input_key),
      packetIndex,
      ...paddedKeys(item.out_key, 8),
      ...paddedIntervals(item.intvel_ms, 8),
    ]),
  // 06 05 参考 CLI 的交错布局函数；CLI 批量流程未调用它，须用 06 0A 实机回读确认。
  macroKeyDownload: (packTotal, packIndex, item, packetIndex) => {
    const payload = [
      toByte(packTotal, "packTotal"),
      toByte(packIndex, "packIndex"),
      toByte(item.MACRO_key_index, "MACRO_key_index"),
      ...parseHexKey(item.input_key),
      toByte(packetIndex, "packetIndex"),
    ];
    const outputKeys = item.out_key || [];
    const intervals = item.intvel_ms || [];
    for (let index = 0; index < outputKeys.length; index += 1) {
      // 录制宏的 UI 只保存相邻按键之间的间隔，末尾事件没有单独的输入框。
      // 协议要求每个输出键都有间隔，缺少时沿用最后一个已保存值。
      const interval = intervals[index] ?? intervals[intervals.length - 1] ?? 0;
      payload.push(
        ...parseHexKey(outputKeys[index]),
        ...uint16Bytes(Number(interval) || 0)
      );
    }
    return buildFrame(0x06, 0x05, payload);
  },
  exchangeKey: (exchangeCount, item) =>
    buildFrame(0x06, 0x01, [
      exchangeCount,
      item.exchange_index,
      ...item.input_key.flatMap(parseHexKey),
      ...new Array(20 - item.input_key.length * 4).fill(0),
      ...item.out_key.flatMap(parseHexKey),
    ]),
  fnKey: (total, items) =>
    buildFrame(0x06, 0x04, [
      total,
      items.length,
      ...items.flatMap((item) => [
        item.Fn_key_index,
        ...parseHexKey(item.input_key),
        ...parseHexKey(item.out_key),
      ]),
    ]),
  swapKey: (total, items) =>
    buildFrame(0x06, 0x06, [
      total,
      // AM Master always uses the fixed swap-key capacity, not items.length.
      11,
      ...items.flatMap((item) => [
        item.swap_key_index,
        ...parseHexKey(item.input_key),
        ...parseHexKey(item.out_key),
      ]),
    ]),
  getKeyLayer: () => buildFrame(0x06, 0x09, [0x00]),
  keyLayerControl: (layerCount) => buildFrame(0x06, 0x08, [layerCount]),
  keyLayerData: (frameIndex, data) =>
    buildFrame(0x06, 0x07, [frameIndex, ...toBytes(data)]),
  pressHold: (item) => {
    const phKey = parseHexKey(item.ph_key_data);
    return buildFrame(0x06, 0x0d, [
      // AM_Master 的按下保持包头是固定容量，不使用 JSON 中的动态分页字段。
      0x00,
      0xc8,
      0x00,
      phKey[3],
      ...phKey,
      ...uint16BE(item.interval_time),
      ...parseHexKey(item.first_key),
      ...parseHexKey(item.second_key),
    ]);
  },
  changeKey: (change) => {
    const payload = [
      toByte(change.layer_num, "layer_num"),
      toByte(change.row_num, "row_num"),
      toByte(change.index, "index"),
      ...parseHexKey(change.old_key),
      ...parseHexKey(change.new_key),
    ];
    return buildFrame(0x06, 0x0b, payload);
  },
  storeChanges: (save = true) => buildFrame(0x06, 0x0c, [save ? 1 : 0]),
};

function uint16BE(value) {
  toUint16(value);
  return [(value >> 8) & 0xff, value & 0xff];
}

function toUint16(value) {
  if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
    throw new RangeError("value must be an unsigned 16-bit integer");
  }
}

export class R6FrameBuffer {
  constructor() {
    this.data = new Uint8Array();
  }

  append(chunk) {
    const bytes = toBytes(chunk);
    const data = new Uint8Array(this.data.length + bytes.length);
    data.set(this.data);
    data.set(bytes, this.data.length);
    this.data = data;
  }

  take() {
    if (this.data.length < R6_CDC.frameSize) return null;
    const frame = this.data.slice(0, R6_CDC.frameSize);
    this.data = this.data.slice(R6_CDC.frameSize);
    return frame;
  }

  clear() {
    this.data = new Uint8Array();
  }
}
